import { normalizeCurrency } from './format.js';

export const RWA_CATALOG_URL = '/api/rwa-catalog';

function catalogAvailability(distributionTypes = []) {
  if (distributionTypes.includes('Onchain Distributed')) return 'cataloged-distributed';
  if (distributionTypes.includes('Onchain Represented')) return 'cataloged-represented';
  return 'cataloged';
}

function uniqueExactIdentities(identities = []) {
  const seen = new Set();
  return identities.filter((identity) => {
    if (identity.identityType !== 'exact-pair' || !identity.issuer || !identity.currency) return false;
    const key = `${identity.issuer}:${normalizeCurrency(identity.currency)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function catalogAssetToProgram(asset, meta = {}) {
  const category = asset.assetClass === 'Stablecoins' ? 'stablecoin' : 'rwa';
  const exactIdentities = uniqueExactIdentities(asset.identities);
  const sourceUpdatedAt = meta.sourceUpdatedAt || meta.retrievedAt || null;

  return {
    id: `rwa-xyz-${asset.id}`,
    name: asset.name,
    ticker: asset.ticker || '—',
    category,
    availability: catalogAvailability(asset.distributionTypes),
    verification: 'cataloged',
    hq: null,
    jurisdiction: asset.jurisdiction,
    summary: `${asset.assetClass} cataloged on XRP Ledger by RWA.xyz as ${asset.distributionTypes.join(' and ').toLowerCase()}.`,
    evidence: 'RWA.xyz supplies this reference identity and classification. AssetGlobe treats it as third-party catalog evidence, not first-party verification of legal rights, backing, or valuation.',
    instruments: exactIdentities.map((identity) => ({
      kind: 'classic',
      issuer: identity.issuer,
      currency: identity.currency,
      ticker: asset.ticker || identity.currency,
      unit: asset.ticker || identity.currency,
      address: identity.address,
      catalogOnly: true,
    })),
    sources: [
      { label: 'RWA.xyz asset record', url: `https://app.rwa.xyz/assets/${encodeURIComponent(asset.slug || asset.ticker || asset.id)}` },
      { label: 'RWA.xyz XRPL network', url: meta.sourceUrl || 'https://app.rwa.xyz/networks/xrp-ledger' },
    ],
    catalog: {
      assetId: asset.id,
      platform: asset.platform,
      issuerName: asset.issuerName,
      assetClass: asset.assetClass,
      jurisdiction: asset.jurisdiction,
      distributionTypes: asset.distributionTypes,
      identities: asset.identities,
      sourceUpdatedAt,
      ledgerAudit: asset.ledgerAudit || null,
    },
  };
}

export function parseRwaCatalog(payload) {
  if (!payload || !Array.isArray(payload.assets) || !payload.meta) {
    throw new Error('RWA.xyz catalog snapshot has an invalid shape.');
  }
  const programs = payload.assets.map((asset) => catalogAssetToProgram(asset, payload.meta));
  return { meta: payload.meta, programs };
}

function identityCovered(identity, matcher) {
  if (!identity.issuer) return [];
  if (identity.identityType === 'exact-pair' && identity.currency) {
    return matcher.matchIssue(identity.issuer, identity.currency);
  }
  return matcher.accountIndex.get(identity.issuer) || [];
}

export function enrichCatalogCoverage(programs, matcher, curatedPrograms = []) {
  const curatedById = new Map(curatedPrograms.map((program) => [program.id, program]));
  return programs.map((program) => {
    const matches = (program.catalog?.identities || []).flatMap((identity) => identityCovered(identity, matcher));
    const curatedIds = [...new Set(matches.map((match) => match.program.id))];
    const uncoveredInstruments = program.instruments.filter((instrument) => (
      matcher.matchIssue(instrument.issuer, instrument.currency).length === 0
    ));
    const explicitProgramId = program.ticker === 'TBILL'
      && program.catalog.platform?.toLowerCase().includes('openeden')
      && curatedById.has('openeden-tbill')
      ? 'openeden-tbill'
      : null;
    const coveredBy = explicitProgramId || curatedIds[0] || null;

    return {
      ...program,
      catalog: {
        ...program.catalog,
        coveredBy,
        coveredByName: coveredBy ? curatedById.get(coveredBy)?.name || null : null,
        exactIdentityCount: program.instruments.length,
        accountOnlyIdentityCount: (program.catalog?.identities || []).filter((identity) => identity.identityType === 'account-only').length,
        liveTrackable: Boolean(coveredBy || program.instruments.length),
        uncoveredInstruments,
      },
    };
  });
}

export function catalogMatcherPrograms(programs) {
  return programs
    .filter((program) => program.catalog?.uncoveredInstruments?.length)
    .map((program) => ({ ...program, instruments: program.catalog.uncoveredInstruments }));
}
